import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  CalendarDays,
  Save,
  FolderOpen,
  ShieldAlert,
  X,
} from 'lucide-react';
import {
  dashboardApi,
  DASHBOARD_KEY,
  TODO_KEY,
} from '../../services/professionalDashboardApi';
import { useOpmaak, vandaagAmsterdam } from './opmaak';

/**
 * Invoerregel boven de vensters: [ actiepunt ] [ Inplannen ] [ Toevoegen ] [ Bekijk alle ].
 * Een actiepunt met een datum verschijnt op die dag in "Vandaag"; zonder datum staat het alleen in het archief.
 */
export const TodoBalk: React.FC<{ archiefLink?: boolean }> = ({
  archiefLink = true,
}) => {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const opmaak = useOpmaak();
  const [tekst, setTekst] = useState('');
  const [datum, setDatum] = useState<string>('');
  const datumRef = useRef<HTMLInputElement>(null);
  // Zelfde query als het archief: de grendelmelding kost geen extra call als het archief al geladen is.
  const { data } = useQuery({
    queryKey: TODO_KEY,
    queryFn: dashboardApi.todos,
    staleTime: 60_000,
  });

  const toevoegen = useMutation({
    mutationFn: () =>
      dashboardApi.maakTodo({ tekst: tekst.trim(), gepland_op: datum || null }),
    onSuccess: () => {
      setTekst('');
      setDatum('');
      toast.success(t('todo.toegevoegd'));
      queryClient.invalidateQueries({ queryKey: TODO_KEY });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
    },
    onError: () => toast.error(t('todo.fout')),
  });

  const verstuur = (e: React.FormEvent) => {
    e.preventDefault();
    if (tekst.trim()) toevoegen.mutate();
  };

  const openKalender = () => {
    const el = datumRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    if (typeof el.showPicker === 'function') el.showPicker();
    else el.focus();
  };

  return (
    <div className="mx-auto max-w-4xl">
      <form
        onSubmit={verstuur}
        className="flex flex-col sm:flex-row gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-card"
      >
        <label className="relative flex-1 min-w-0">
          <span className="sr-only">{t('todo.placeholder')}</span>
          <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
          <input
            value={tekst}
            onChange={e => setTekst(e.target.value)}
            maxLength={500}
            placeholder={t('todo.placeholder')}
            className="w-full rounded-xl border-0 bg-canvas pl-9 pr-3 py-2.5 text-sm text-navy-900 placeholder:text-navy-400 focus:ring-2 focus:ring-navy-500"
          />
        </label>
        <div className="flex gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={openKalender}
              className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-medium whitespace-nowrap ${
                datum
                  ? 'border-navy-900 bg-navy-50 text-navy-900'
                  : 'border-gray-200 text-navy-700 hover:border-navy-300'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              {datum
                ? t('todo.inplannenOp', { datum: opmaak.dag(datum) })
                : t('todo.inplannen')}
            </button>
            <input
              ref={datumRef}
              type="date"
              min={vandaagAmsterdam()}
              value={datum}
              onChange={e => setDatum(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              className="absolute inset-0 opacity-0 pointer-events-none"
            />
            {datum && (
              <button
                type="button"
                onClick={() => setDatum('')}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-gray-300 flex items-center justify-center text-navy-500 hover:text-navy-900"
                aria-label={t('todo.geenDatum')}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!tekst.trim() || toevoegen.isPending}
            className="inline-flex items-center gap-1.5 rounded-xl bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50 whitespace-nowrap"
          >
            <Save className="w-4 h-4" />
            {t('todo.toevoegen')}
          </button>
          {archiefLink && (
            <Link
              to="/professional/dashboard/todo"
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium text-navy-800 hover:bg-navy-50 whitespace-nowrap"
            >
              <FolderOpen className="w-4 h-4" />
              <span className="hidden md:inline">{t('todo.bekijkAlle')}</span>
            </Link>
          )}
        </div>
      </form>
      {data?.tekstgrendel && (
        <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-navy-500">
          <ShieldAlert className="w-3.5 h-3.5 text-gold-700" />
          {t('todo.tekstgrendel')}
        </p>
      )}
    </div>
  );
};
